import React, { useState } from 'react';
import { PictogramPicker } from './PictogramPicker';
import type { PictogramKind } from '../constants/pictograms';
import { mediaSrc } from '../utils/media';
import {
  BUFF_BONUS_FIELDS,
  type BuffBonuses,
  formatBuffEffect,
} from '../utils/itemStats';

type BuffFieldKey = (typeof BUFF_BONUS_FIELDS)[number]['key'] | 'heal' | 'heal_count';

const EMPTY_BONUSES: BuffBonuses = {
  damage: 0,
  movement: 0,
  range: 0,
  armor: 0,
  health: 0,
  mana: 0,
  heal: 0,
  heal_count: 0,
};

interface CustomBuffDialogProps {
  characterName: string;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (payload: { name: string; bonuses: BuffBonuses; description: string; icon_url: string | null }) => void;
}

export function CustomBuffDialog({
  characterName,
  busy = false,
  error = null,
  onCancel,
  onSubmit,
}: CustomBuffDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [iconKind, setIconKind] = useState<PictogramKind>('item');
  const [bonuses, setBonuses] = useState<BuffBonuses>(EMPTY_BONUSES);
  const preview = formatBuffEffect(bonuses);
  const canSubmit = Boolean(name.trim()) && Boolean(preview) && !busy;

  const setBonus = (key: BuffFieldKey, raw: string) => {
    const parsed = parseInt(raw, 10);
    setBonuses(prev => ({
      ...prev,
      [key]: Number.isFinite(parsed) ? parsed : 0,
    }));
  };

  return (
    <div className="modal-overlay" data-gamepad-overlay="modal" onClick={onCancel} role="presentation">
      <div
        className="modal-content confirm-dialog custom-buff-dialog"
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-buff-title"
      >
        <div className="modal-header">
          <h2 id="custom-buff-title">Buff für {characterName}</h2>
          <button type="button" className="modal-close" data-gamepad-dismiss onClick={onCancel} aria-label="Schließen">
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="confirm-dialog-message">
            Wie ein Trank-Effekt: Name wählen und Werte setzen. Der Buff bleibt aktiv, bis du ihn beendest.
          </p>
          <label className="custom-buff-field" htmlFor="custom-buff-name">
            Name
            <input
              id="custom-buff-name"
              type="text"
              value={name}
              maxLength={255}
              disabled={busy}
              placeholder="z. B. Segen des Meisters"
              onChange={event => setName(event.target.value)}
            />
          </label>
          <fieldset className="custom-buff-bonuses custom-buff-icons" disabled={busy}>
            <legend>Icon</legend>
            <div className="custom-buff-icon-tools">
              <div className="custom-buff-icon-kinds" role="group" aria-label="Icon-Art">
                <button
                  type="button"
                  className={iconKind === 'item' ? 'selected' : ''}
                  aria-pressed={iconKind === 'item'}
                  onClick={() => setIconKind('item')}
                >
                  Gegenstand
                </button>
                <button
                  type="button"
                  className={iconKind === 'spell' ? 'selected' : ''}
                  aria-pressed={iconKind === 'spell'}
                  onClick={() => setIconKind('spell')}
                >
                  Zauber
                </button>
              </div>
              {iconUrl ? (
                <img className="custom-buff-icon-preview" src={mediaSrc(iconUrl)} alt="" />
              ) : (
                <span className="custom-buff-icon-standard">Standard</span>
              )}
              <button type="button" onClick={() => setIconUrl(null)} disabled={!iconUrl}>
                Zurücksetzen
              </button>
            </div>
            <PictogramPicker kind={iconKind} value={iconUrl} onChange={icon => setIconUrl(icon.src)} />
          </fieldset>
          <fieldset className="custom-buff-bonuses" disabled={busy}>
            <legend>Werte</legend>
            <div className="custom-buff-bonus-grid">
              {BUFF_BONUS_FIELDS.map(field => (
                <label key={field.key} className="custom-buff-bonus" htmlFor={`custom-buff-${field.key}`}>
                  <span aria-hidden>{field.icon}</span> {field.label}
                  <input
                    id={`custom-buff-${field.key}`}
                    type="number"
                    step={1}
                    value={bonuses[field.key] ?? 0}
                    onChange={event => setBonus(field.key, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="custom-buff-bonuses" disabled={busy}>
            <legend>Heilung</legend>
            <p className="form-help-text">Heilt beim Auslösen. Anzahl ist, wie oft der Buff heilt.</p>
            <div className="custom-buff-bonus-grid">
              <label className="custom-buff-bonus" htmlFor="custom-buff-heal">
                <span aria-hidden>💚</span> Heilung
                <input
                  id="custom-buff-heal"
                  type="number"
                  step={1}
                  min={0}
                  value={bonuses.heal ?? 0}
                  onChange={event => setBonus('heal', event.target.value)}
                />
              </label>
              <label className="custom-buff-bonus" htmlFor="custom-buff-heal-count">
                <span aria-hidden>🔁</span> Anzahl
                <input
                  id="custom-buff-heal-count"
                  type="number"
                  step={1}
                  min={0}
                  value={bonuses.heal_count ?? 0}
                  onChange={event => setBonus('heal_count', event.target.value)}
                />
              </label>
            </div>
          </fieldset>
          <label className="custom-buff-field" htmlFor="custom-buff-description">
            Beschreibung (optional)
            <textarea
              id="custom-buff-description"
              rows={2}
              value={description}
              disabled={busy}
              placeholder="Kurztext für den Tooltip"
              onChange={event => setDescription(event.target.value)}
            />
          </label>
          <p className="custom-buff-preview" aria-live="polite">
            Effekt: {preview || '— noch keine Werte —'}
          </p>
          {error && <p className="form-error" role="alert">{error}</p>}
        </div>
        <div className="modal-footer">
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={busy}>
            Abbrechen
          </button>
          <button
            type="button"
            className="create-btn"
            disabled={!canSubmit}
            onClick={() => onSubmit({
              name: name.trim(),
              bonuses,
              description: description.trim(),
              icon_url: iconUrl,
            })}
          >
            Buff setzen
          </button>
        </div>
      </div>
    </div>
  );
}
